import { VideoStatus } from '../types.ts/video';
import { extractErrorInfo } from '../utility/error';
import { LogManager } from '../utility/Logger';
import { generateTempClipId } from './WriteQueueService';

import type { AssociatedVideo, OtherVideo } from '../types.ts/story';
import type { IVideoCreatorStore } from '@src/stores/IVideoCreatorStore';

const logger = LogManager.getLogger('service:clipCopy');

/**
 * Reset a clip to a fresh, unpublished editing draft while keeping its
 * presentational data (thumbnail, source, aspect ratio, …). Mirrors the reset
 * `VideoRepository.copyParentAndAssociatedVideos` performs server-side on the
 * parent and every variant — keep the two in sync, otherwise the copy briefly
 * shows as a published/"Final" clip when it isn't.
 */
const toDraft = <T extends object>(v: T) => ({
  ...v,
  videoStatus: VideoStatus.Editing,
  videoFilePrimary: undefined,
  lastPublishJson: undefined,
  // Not yet shared or captioned — drop the source's references so the copy
  // doesn't momentarily appear shared in Content Studio.
  _allReferencingSharedContents: [],
  _allReferencingCaptions: [],
});

/**
 * Duplicate an existing clip from Content Studio with optimistic UI.
 *
 * Mirrors the clip-producer save pattern in AIClipProducer: the service
 * orchestrates the repository calls and drives the store's optimistic
 * primitives (`addOptimisticVideos` / `replaceOptimisticVideoId` /
 * `removeOptimisticVideo`). An optimistic "Saving…" card appears immediately,
 * then its temp ID is swapped for the real one once the copy completes. Unlike
 * `copyVideo`, it does not load the copy into the editor — Content Studio stays
 * on the dashboard.
 */
export async function copyClipWithOptimisticUI(
  videoCreator: IVideoCreatorStore,
  sourceVideo: OtherVideo,
  newTitle: string,
): Promise<void> {
  const { story } = videoCreator;
  if (!story || !sourceVideo.id) return;

  const tempId = generateTempClipId();
  const lastAction = {
    editor: videoCreator.rootStore.userIdentityStore.currentEditor,
    date: new Date().getTime(),
    type: 'save' as const,
  };

  // Clone the source clip and its variants as fresh drafts under the new name.
  // Each variant keeps its own temp id (not the source's, which would collide
  // with the original card) so the copy's aspect-ratio chips render right away;
  // the temp ids are swapped for the real ones once the copy completes.
  const sourceAssociated = sourceVideo.associatedVideos || [];
  const optimisticAssociated = sourceAssociated.map(av => ({
    ...toDraft(av),
    id: generateTempClipId(),
  })) as AssociatedVideo[];

  const optimisticVideo = {
    ...toDraft(sourceVideo),
    id: tempId,
    title: newTitle,
    _publishedAt: undefined,
    lastActionJson: lastAction,
    associatedVideos: optimisticAssociated,
  } as OtherVideo;

  videoCreator.addOptimisticVideos([optimisticVideo]);
  // Blink the green "recently saved" border on the optimistic card right away,
  // matching the clip-producer flow.
  videoCreator.rootStore.contentStudioUIStore.markClipsSaved([tempId]);

  try {
    const { id, slug, hash, associatedVideoIdPairs } =
      await videoCreator.datoClientStore.videoRepository.copyParentAndAssociatedVideos(
        sourceVideo.id,
        newTitle,
        'content-studio',
        JSON.stringify(lastAction),
      );
    await videoCreator.datoClientStore.storyRepository.addVideoToStory(
      story.id,
      id,
    );
    await videoCreator.replaceOptimisticVideoId(
      tempId,
      id,
      slug,
      hash,
      replaceOptimisticIdsWithReal(
        optimisticAssociated,
        associatedVideoIdPairs,
        sourceAssociated,
      ),
    );
    // Carry the highlight onto the real card once the temp ID is swapped in.
    videoCreator.rootStore.contentStudioUIStore.markClipsSaved([id]);
  } catch (err) {
    // Roll back the optimistic card and its highlight so a failed copy doesn't
    // linger (the temp ID would otherwise stay in recentlySavedClipIds).
    videoCreator.removeOptimisticVideo(tempId);
    videoCreator.rootStore.contentStudioUIStore.dismissClipHighlight(tempId);
    logger.error('Error copying clip', extractErrorInfo(err));
  }
}

// Stamp the real ids onto the drafted variants (matched by source id), so
// the card keeps its cloned variant data but points at the real records.
// A linear find is fine — a clip has at most a couple of variants.
function replaceOptimisticIdsWithReal(
  optimisticAssociated: AssociatedVideo[],
  associatedVideoIdPairs: { sourceId: string; copyId: string }[],
  sourceAssociated: AssociatedVideo[],
) {
  return optimisticAssociated.map((draftAv, i) => {
    const real = associatedVideoIdPairs.find(
      p => p.sourceId === sourceAssociated[i].id,
    );
    return real ? { ...draftAv, id: real.copyId } : draftAv;
  });
}
